/**
 * Single source of truth for turning an API failure into user-facing text.
 *
 * The backend's validation middleware (`sendValidationError`) returns field
 * errors keyed by the *flattened* request path:
 *
 *   { success: false, message: "Validation failed",
 *     errors: { "body.bio": ["\"bio\" length must be at least 30..."],
 *               "body.services": ["Services must be one of: ..."] } }
 *
 * Note the keys are dotted strings, not a nested `errors.body` object — reading
 * `errors.body` yields undefined and leaves the user staring at the useless
 * top-level "Validation failed". We handle both shapes here so no screen has to
 * guess again.
 */

const GENERIC_FALLBACK = "Something went wrong. Please try again.";

// Top-level messages that tell the user nothing on their own. When we have real
// field errors we drop these; when we don't, we swap in something actionable.
const UNHELPFUL_MESSAGES = {
  "validation failed": "Please check the details you entered and try again.",
  "bad request": "Please check the details you entered and try again.",
  "invalid request": "Please check the details you entered and try again.",
};

// Request paths carry no meaning for the user — strip them before labelling.
const SECTION_KEYS = new Set(["body", "query", "params", "headers", "cookies"]);

// Field names whose humanised form would read badly or ambiguously.
const FIELD_LABELS = {
  bio: "Bio",
  dob: "Date of birth",
  email: "Email address",
  id: "ID",
  idDocument: "ID document",
  idNumber: "ID number",
  otp: "Verification code",
  phone: "Phone number",
  phoneNumber: "Phone number",
  profilePicture: "Profile photo",
  url: "URL",
};

const isFilledString = (value) =>
  typeof value === "string" && value.trim().length > 0;

/** Joi quotes field names (`"bio" length must be...`); the quotes read as noise. */
const stripQuotes = (message) => message.replace(/["'`]/g, "").trim();

/** `body.vehicle.year` -> `year`; the leading sections are plumbing. */
const toFieldName = (key) => {
  if (!isFilledString(key)) return "";
  const segments = key
    .split(".")
    .map((segment) => segment.trim())
    .filter((segment) => segment && !SECTION_KEYS.has(segment));
  // Numeric tail means an array index (`services.0`) — the parent is the field.
  const meaningful = segments.filter((segment) => !/^\d+$/.test(segment));
  return meaningful[meaningful.length - 1] || "";
};

const toFieldLabel = (field) => {
  if (!field) return "";
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  const spaced = field
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

const capitalise = (message) =>
  message.charAt(0).toUpperCase() + message.slice(1);

/**
 * Rewrites a raw Joi message into a sentence that names the field the way the
 * form labels it: `"bio" length must be at least 30 characters long`
 * becomes `Bio must be at least 30 characters long`.
 */
const formatFieldMessage = (key, rawMessage) => {
  const cleaned = stripQuotes(rawMessage);
  if (!cleaned) return "";

  const field = toFieldName(key);
  if (!field) return capitalise(cleaned);

  const label = toFieldLabel(field);
  const lowered = cleaned.toLowerCase();

  let body = cleaned;
  if (lowered.startsWith(field.toLowerCase())) {
    body = cleaned.slice(field.length).trim();
  } else if (lowered.startsWith(label.toLowerCase())) {
    body = cleaned.slice(label.length).trim();
  } else if (lowered.includes(label.toLowerCase())) {
    // Already reads naturally (e.g. "Services must be one of: ...").
    return capitalise(cleaned);
  }

  // Joi's "length must be" / "must contain" phrasing doubles up once the field
  // name becomes a proper label.
  body = body.replace(/^length must be/i, "must be");

  if (!body) return `${label} is invalid.`;
  return `${label} ${body}`.replace(/\s+/g, " ").trim();
};

/**
 * Walks whatever `errors` shape came back — dotted keys, nested objects, arrays
 * of strings, or Joi detail objects — and flattens it to display strings.
 */
const collectFieldMessages = (node, key, out) => {
  if (node === null || node === undefined) return;

  if (typeof node === "string") {
    const message = formatFieldMessage(key, node);
    if (message) out.push(message);
    return;
  }

  if (Array.isArray(node)) {
    node.forEach((item) => collectFieldMessages(item, key, out));
    return;
  }

  if (typeof node !== "object") return;

  // Joi detail object: { message, path: [...] }
  if (isFilledString(node.message)) {
    const path = Array.isArray(node.path) ? node.path.join(".") : key;
    collectFieldMessages(node.message, path, out);
    return;
  }

  Object.entries(node).forEach(([childKey, value]) => {
    collectFieldMessages(value, childKey, out);
  });
};

const dedupe = (messages) => {
  const seen = new Set();
  return messages.filter((message) => {
    const marker = message.toLowerCase();
    if (seen.has(marker)) return false;
    seen.add(marker);
    return true;
  });
};

/** Axios noise that must never reach a user verbatim. */
const resolveTransportMessage = (error) => {
  if (error?.code === "ECONNABORTED") {
    return "The request timed out. Please try again.";
  }
  if (error?.message === "Network Error" || error?.code === "ERR_NETWORK") {
    return "Network error. Check your connection and try again.";
  }
  if (/^request failed with status code/i.test(error?.message || "")) {
    return "";
  }
  return isFilledString(error?.message) ? error.message.trim() : "";
};

/**
 * Every user-facing message an API error carries, most specific first.
 * Always returns at least one message.
 */
export const getApiErrorMessages = (error, fallback = GENERIC_FALLBACK) => {
  const data = error?.response?.data || error?.data || {};

  const fieldMessages = [];
  collectFieldMessages(data?.errors, "", fieldMessages);
  if (fieldMessages.length) return dedupe(fieldMessages);

  const apiMessage = [data?.message, data?.error].find(isFilledString);
  if (apiMessage) {
    const cleaned = stripQuotes(apiMessage);
    const replacement = UNHELPFUL_MESSAGES[cleaned.toLowerCase()];
    return [replacement || capitalise(cleaned)];
  }

  const transportMessage = resolveTransportMessage(error);
  if (transportMessage) return [transportMessage];

  return [isFilledString(fallback) ? fallback : GENERIC_FALLBACK];
};

/**
 * The same result as a single string, for callers that store or return an error
 * rather than toasting it.
 */
export const getApiErrorMessage = (error, fallback = GENERIC_FALLBACK) =>
  getApiErrorMessages(error, fallback).join(" ");

export { GENERIC_FALLBACK };
